import { Granularity, Grouping } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/v1/aiunits_pb';
import { UsageDataResult } from '../types';
import { useUsageData } from './useUsageData';

/**
 * Custom hook to fetch FILTERED usage data for chart display
 *
 * This hook fetches usage data with filters applied for the CURRENT date range
 * and grouping only. Unlike useMultiDimensionalPrefetch, it does NOT:
 * - Prefetch multiple time ranges
 * - Prefetch multiple groupings
 * - Use staged loading
 *
 * It's a simple, single query for displaying filtered chart data.
 * Filter options should come from unfiltered data (useMultiDimensionalPrefetch).
 *
 * @param selectedServices Applied service filters
 * @param selectedWorkspaces Applied workspace filters
 * @param selectedUsers Applied user filters
 * @param selectedTags Applied tag filters
 * @param currentTimeRange Current date range parameters
 * @param groupBy Current grouping selection
 * @returns Object containing filtered usage data, loading state, error, and refetch function
 */
export function useFilteredUsageData(
  selectedServices: string[],
  selectedWorkspaces: string[],
  selectedUsers: string[],
  selectedTags: string[],
  currentTimeRange: {
    startTime: Date;
    endTime: Date;
    granularity: Granularity;
  },
  groupBy: Grouping
): UsageDataResult {
  const { startTime, endTime, granularity } = currentTimeRange;

  // Single query for the current date range + grouping + filters
  const { usageData, isLoading, error, refetch } = useUsageData({
    startTime,
    endTime,
    granularity,
    groupBy,
    selectedServices,
    selectedWorkspaces,
    selectedUsers,
    selectedTags,
  });

  return {
    usageData,
    // No workspace/user data - filter options come from unfiltered data
    workspaceData: undefined,
    userData: undefined,
    isLoading,
    error,
    refetch,
  };
}
